import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { RequirePermission } from '@/components/RequirePermission';
import { Permissions } from '@jerico/shared';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { ThemeProvider } from '@/components/ui/ThemeProvider';
import { ConfirmProvider } from '@/contexts/ConfirmContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { StealthGate } from '@/components/StealthGate';

// Loading spinner for lazy routes
function PageLoader() {
  return (
    <div className="flex min-h-[200px] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
    </div>
  );
}

// Helper for lazy-loading named exports
function lazyNamed<T extends Record<string, any>>(
  factory: () => Promise<T>,
  name: keyof T,
) {
  return lazy(() => factory().then((m) => ({ default: m[name] })));
}

// Auth routes
const LoginPage = lazyNamed(() => import('@/routes/login'), 'LoginPage');
const ForgotPasswordPage = lazyNamed(() => import('@/routes/forgot-password'), 'ForgotPasswordPage');
const AuthCallbackPage = lazyNamed(() => import('@/routes/auth/callback'), 'AuthCallbackPage');
const AcceptInvitePage = lazyNamed(() => import('@/routes/auth/accept-invite'), 'AcceptInvitePage');
const MfaChallengePage = lazy(() => import('@/routes/mfa-challenge'));

// Onboarding
const OnboardingPage = lazyNamed(() => import('@/routes/onboarding/index'), 'OnboardingPage');
const SetupWizard = lazyNamed(() => import('@/routes/onboarding/setup-wizard'), 'SetupWizard');
const IntegrationSetupPage = lazyNamed(() => import('@/routes/onboarding/integration-setup'), 'IntegrationSetupPage');

// Marketing pages
const LandingPage = lazyNamed(() => import('@/routes/marketing/landing'), 'LandingPage');
const HowItWorksPage = lazyNamed(() => import('@/routes/marketing/how-it-works'), 'HowItWorksPage');
const EarlyAccessPage = lazyNamed(() => import('@/routes/marketing/early-access'), 'EarlyAccessPage');
const AboutPage = lazyNamed(() => import('@/routes/marketing/about'), 'AboutPage');
const TermsPage = lazyNamed(() => import('@/routes/marketing/terms'), 'TermsPage');
const PrivacyPolicyPage = lazyNamed(() => import('@/routes/marketing/privacy'), 'PrivacyPolicyPage');
const AIDisclaimerPage = lazyNamed(() => import('@/routes/marketing/ai-disclaimer'), 'AIDisclaimerPage');
const NotFoundPage = lazyNamed(() => import('@/routes/marketing/not-found'), 'NotFoundPage');

// App pages
const DashboardPage = lazyNamed(() => import('@/routes/dashboard'), 'DashboardPage');
const ClientsPage = lazyNamed(() => import('@/routes/clients/index'), 'ClientsPage');
const ClientDetailPage = lazyNamed(() => import('@/routes/clients/[clientId]'), 'ClientDetailPage');
const ContactsPage = lazyNamed(() => import('@/routes/contacts/index'), 'ContactsPage');
const ContactDetailPage = lazyNamed(() => import('@/routes/contacts/[contactId]'), 'ContactDetailPage');
const OrganizationsPage = lazyNamed(() => import('@/routes/organizations/index'), 'OrganizationsPage');
const OrganizationDetailPage = lazyNamed(() => import('@/routes/organizations/[organizationId]'), 'OrganizationDetailPage');
const ConversationsPage = lazyNamed(() => import('@/routes/conversations/index'), 'ConversationsPage');
const ConversationDetailPage = lazyNamed(() => import('@/routes/conversations/[conversationId]'), 'ConversationDetailPage');
const MattersPage = lazyNamed(() => import('@/routes/matters/index'), 'MattersPage');
const MatterDetailPage = lazyNamed(() => import('@/routes/matters/[matterId]'), 'MatterDetailPage');
const DeadlinesPage = lazyNamed(() => import('@/routes/deadlines/index'), 'DeadlinesPage');
const DocumentsPage = lazyNamed(() => import('@/routes/documents/index'), 'DocumentsPage');
const InvoicesPage = lazyNamed(() => import('@/routes/invoices/index'), 'InvoicesPage');
const CreateInvoicePage = lazyNamed(() => import('@/routes/invoices/create'), 'CreateInvoicePage');
const InvoiceDetailPage = lazyNamed(() => import('@/routes/invoices/[invoiceId]'), 'InvoiceDetailPage');
const ChannelsPage = lazyNamed(() => import('@/routes/channels/index'), 'ChannelsPage');
const ChannelDetailPage = lazyNamed(() => import('@/routes/channels/[channelId]'), 'ChannelDetailPage');
const LeadMagnetsPage = lazyNamed(() => import('@/routes/lead-magnets/index'), 'LeadMagnetsPage');
const CampaignsPage = lazyNamed(() => import('@/routes/campaigns/index'), 'CampaignsPage');
const CreateCampaignPage = lazyNamed(() => import('@/routes/campaigns/create'), 'CreateCampaignPage');
const CampaignDetailPage = lazyNamed(() => import('@/routes/campaigns/[campaignId]'), 'CampaignDetailPage');
const BotConfigPage = lazyNamed(() => import('@/routes/bot-config'), 'BotConfigPage');
const SettingsPage = lazyNamed(() => import('@/routes/settings'), 'SettingsPage');
const EmailsPage = lazyNamed(() => import('@/routes/emails/index'), 'EmailsPage');
const EmailDetailPage = lazyNamed(() => import('@/routes/emails/[emailId]'), 'EmailDetailPage');
const ComposeEmailPage = lazyNamed(() => import('@/routes/emails/compose'), 'ComposeEmailPage');
const EmailTriageSettingsPage = lazyNamed(() => import('@/routes/emails/triage-settings'), 'EmailTriageSettingsPage');
const EmailTemplatesPage = lazyNamed(() => import('@/routes/email-templates/index'), 'EmailTemplatesPage');
const EmailTemplateDetailPage = lazyNamed(() => import('@/routes/email-templates/[templateId]'), 'EmailTemplateDetailPage');
const UsersPage = lazyNamed(() => import('@/routes/users/index'), 'UsersPage');
const ProfilePage = lazyNamed(() => import('@/routes/profile'), 'ProfilePage');
const TimesheetPage = lazyNamed(() => import('@/routes/timesheet/index'), 'TimesheetPage');
const GrowthIndexPage = lazyNamed(() => import('@/routes/growth/index'), 'GrowthIndexPage');
const PlaybooksIndexPage = lazyNamed(() => import('@/routes/growth/playbooks/index'), 'PlaybooksIndexPage');
const PlaybookDetailPage = lazyNamed(() => import('@/routes/growth/playbooks/[playbookId]'), 'PlaybookDetailPage');
const RateCardPage = lazyNamed(() => import('@/routes/billing/rate-card'), 'RateCardPage');
const MemoryPage = lazyNamed(() => import('@/routes/memory/index'), 'MemoryPage');
const AIChat = lazyNamed(() => import('@/routes/ai-chat/index'), 'AIChat');
const WorkflowsPage = lazyNamed(() => import('@/routes/workflows/index'), 'WorkflowsPage');
const ClientPortalPage = lazyNamed(() => import('@/routes/portal/index'), 'ClientPortalPage');
const CalendarPage = lazyNamed(() => import('@/routes/calendar/index'), 'CalendarPage');
const TranscriptionPage = lazyNamed(() => import('@/routes/transcription/index'), 'TranscriptionPage');
const AIAnalyticsPage = lazyNamed(() => import('@/routes/ai-analytics/index'), 'AIAnalyticsPage');
const CompliancePage = lazyNamed(() => import('@/routes/compliance/index'), 'CompliancePage');
const I18nSettingsPage = lazyNamed(() => import('@/routes/settings-i18n/index'), 'I18nSettingsPage');
const NotificationsPage = lazyNamed(() => import('@/routes/notifications/index'), 'NotificationsPage');
const TrustAccountingPage = lazyNamed(() => import('@/routes/trust/index'), 'TrustAccountingPage');
const EFilingPage = lazyNamed(() => import('@/routes/efiling/index'), 'EFilingPage');
const DocGenPage = lazyNamed(() => import('@/routes/docgen/index'), 'DocGenPage');
const IntakeFormsPage = lazyNamed(() => import('@/routes/intake/index'), 'IntakeFormsPage');
const PublicIntakeForm = lazyNamed(() => import('@/routes/intake/public'), 'PublicIntakeForm');
const ReportsPage = lazyNamed(() => import('@/routes/reports/index'), 'ReportsPage');
const AuditPage = lazyNamed(() => import('@/routes/audit/index'), 'AuditPage');
const TasksPage = lazyNamed(() => import('@/routes/tasks/index'), 'TasksPage');
const ConflictSearchPage = lazyNamed(() => import('@/routes/conflicts/index'), 'ConflictSearchPage');
const BulkOperationsPage = lazyNamed(() => import('@/routes/bulk/index'), 'BulkOperationsPage');
const SocialConnectionsPage = lazyNamed(() => import('@/routes/social/index'), 'SocialConnectionsPage');
const SocialPostsPage = lazyNamed(() => import('@/routes/social-posts/index'), 'SocialPostsPage');
const ContentStudioPage = lazyNamed(() => import('@/routes/content-studio/index'), 'ContentStudioPage');
const MarketingAnalyticsPage = lazyNamed(() => import('@/routes/marketing-analytics/index'), 'MarketingAnalyticsPage');
const LeadScoringPage = lazyNamed(() => import('@/routes/lead-scoring/index'), 'LeadScoringPage');
const DashboardWidgetsPage = lazyNamed(() => import('@/routes/dashboard-widgets/index'), 'DashboardWidgetsPage');
const ExpensesPage = lazyNamed(() => import('@/routes/expenses/index'), 'ExpensesPage');
const KnowledgeBasePage = lazyNamed(() => import('@/routes/knowledge-base/index'), 'KnowledgeBasePage');
const CourtDeadlineCalculatorPage = lazyNamed(() => import('@/routes/court-deadlines/index'), 'CourtDeadlineCalculatorPage');
const SignaturesPage = lazyNamed(() => import('@/routes/signatures/index'), 'SignaturesPage');
const CommunicationLogPage = lazyNamed(() => import('@/routes/communications/index'), 'CommunicationLogPage');
const CampaignSocialPage = lazyNamed(() => import('@/routes/campaign-social/index'), 'CampaignSocialPage');
const IntegrationsSettingsPage = lazyNamed(() => import('@/routes/settings/integrations'), 'IntegrationsSettingsPage');
const QuickBooksSettingsPage = lazyNamed(() => import('@/routes/settings/quickbooks'), 'QuickBooksSettingsPage');
const SmsPage = lazyNamed(() => import('@/routes/sms/index'), 'SmsPage');
const TwilioSettingsPage = lazyNamed(() => import('@/routes/settings/twilio'), 'TwilioSettingsPage');
const KanbanPage = lazyNamed(() => import('@/routes/kanban/index'), 'KanbanPage');
const StorefrontPage = lazyNamed(() => import('@/routes/storefront/index'), 'StorefrontPage');
const LegalResearchPage = lazyNamed(() => import('@/routes/legal-research/index'), 'LegalResearchPage');
const LegalResearchDetailPage = lazyNamed(() => import('@/routes/legal-research/[researchId]'), 'LegalResearchDetailPage');
const ApprovalsPage = lazyNamed(() => import('@/routes/approvals/index'), 'Approvals');

// Default export pages
const ImportWizardPage = lazy(() => import('@/routes/import/index'));
const CustomFieldsSettings = lazy(() => import('@/routes/settings/custom-fields'));
const ApiKeysPage = lazy(() => import('@/routes/settings/api-keys'));
const WebhooksPage = lazy(() => import('@/routes/settings/webhooks'));
const MfaSettingsPage = lazy(() => import('@/routes/settings/mfa'));
const VendorsPage = lazyNamed(() => import('@/routes/settings/vendors'), 'VendorsPage');
const ComplianceCalendarPage = lazyNamed(() => import('@/routes/settings/compliance-calendar'), 'ComplianceCalendarPage');
const ContractReviewList = lazy(() => import('@/routes/contract-review/index'));
const ContractReviewDetail = lazy(() => import('@/routes/contract-review/[reviewId]'));
const PaymentPlansPage = lazy(() => import('@/routes/billing/payment-plans'));
const RevenueAnalyticsPage = lazy(() => import('@/routes/analytics/revenue'));
const ActivityFeedPage = lazy(() => import('@/routes/activity/index'));
const InboxPage = lazyNamed(() => import('@/routes/inbox/index'), 'InboxPage');

// Admin
const AdminDashboardPage = lazyNamed(() => import('@/routes/admin/index'), 'AdminDashboardPage');
const AdminTenantsPage = lazyNamed(() => import('@/routes/admin/tenants'), 'AdminTenantsPage');
const AdminTenantDetailPage = lazyNamed(() => import('@/routes/admin/tenant-detail'), 'AdminTenantDetailPage');

export function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
    <ConfirmProvider>
    <BrowserRouter>
      <StealthGate>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Marketing routes */}
        <Route element={<MarketingLayout />}>
          <Route index element={<LandingPage />} />
          <Route path="how-it-works" element={<HowItWorksPage />} />
          <Route path="early-access" element={<EarlyAccessPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPolicyPage />} />
          <Route path="ai-disclaimer" element={<AIDisclaimerPage />} />
          {/* Redirects — preserve old URLs */}
          <Route path="demo" element={<Navigate to="/early-access" replace />} />
          <Route path="features" element={<Navigate to="/" replace />} />
          <Route path="features/all" element={<Navigate to="/" replace />} />
          <Route path="team-members" element={<Navigate to="/" replace />} />
          <Route path="ai-capabilities" element={<Navigate to="/" replace />} />
          <Route path="for-solo-practitioners" element={<Navigate to="/" replace />} />
          <Route path="for-growing-firms" element={<Navigate to="/" replace />} />
          <Route path="integrations" element={<Navigate to="/" replace />} />
          <Route path="compare" element={<Navigate to="/" replace />} />
          <Route path="coming-soon" element={<Navigate to="/" replace />} />
          <Route path="blog" element={<Navigate to="/" replace />} />
          <Route path="blog/*" element={<Navigate to="/" replace />} />
          <Route path="case-studies" element={<Navigate to="/" replace />} />
          <Route path="case-studies/*" element={<Navigate to="/" replace />} />
          <Route path="pricing" element={<Navigate to="/" replace />} />
        </Route>

        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/signup" element={<Navigate to="/early-access" replace />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/invite" element={<AcceptInvitePage />} />
        <Route path="/mfa-challenge" element={<MfaChallengePage />} />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        } />

        {/* Protected app routes */}
        <Route
          path="app"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="setup" element={<SetupWizard />} />
          <Route path="setup/integrations" element={<IntegrationSetupPage />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/:clientId" element={<ClientDetailPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="contacts/:contactId" element={<ContactDetailPage />} />
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="organizations/:organizationId" element={<OrganizationDetailPage />} />
          <Route path="conversations" element={<ConversationsPage />} />
          <Route path="conversations/:conversationId" element={<ConversationDetailPage />} />
          <Route path="matters" element={<MattersPage />} />
          <Route path="matters/:matterId" element={<MatterDetailPage />} />
          <Route path="deadlines" element={<DeadlinesPage />} />
          <Route path="timesheet" element={<TimesheetPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="invoices/create" element={<CreateInvoicePage />} />
          <Route path="invoices/:invoiceId" element={<InvoiceDetailPage />} />
          <Route path="channels" element={<ChannelsPage />} />
          <Route path="channels/:channelId" element={<ChannelDetailPage />} />
          <Route path="lead-magnets" element={<LeadMagnetsPage />} />
          <Route path="campaigns" element={<CampaignsPage />} />
          <Route path="campaigns/create" element={<CreateCampaignPage />} />
          <Route path="campaigns/:campaignId" element={<CampaignDetailPage />} />
          <Route path="inbox" element={<RequirePermission permission={Permissions.VIEW_MESSAGES}><InboxPage /></RequirePermission>} />
          <Route path="emails" element={<EmailsPage />} />
          <Route path="emails/compose" element={<ComposeEmailPage />} />
          <Route path="emails/triage-settings" element={<EmailTriageSettingsPage />} />
          <Route path="emails/:emailId" element={<EmailDetailPage />} />
          <Route path="email-templates" element={<EmailTemplatesPage />} />
          <Route path="email-templates/:templateId" element={<EmailTemplateDetailPage />} />
          <Route path="bot-config" element={<BotConfigPage />} />
          <Route path="memory" element={<RequirePermission permission={Permissions.MANAGE_BOT}><MemoryPage /></RequirePermission>} />
          <Route path="ai-chat" element={<AIChat />} />
          <Route path="ai-analytics" element={<RequirePermission permission={Permissions.MANAGE_BOT}><AIAnalyticsPage /></RequirePermission>} />
          <Route path="workflows" element={<RequirePermission permission={Permissions.MANAGE_BOT}><WorkflowsPage /></RequirePermission>} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="transcription" element={<TranscriptionPage />} />
          <Route path="compliance" element={<RequirePermission permission={Permissions.MANAGE_MATTERS}><CompliancePage /></RequirePermission>} />
          <Route path="portal" element={<ClientPortalPage />} />
          <Route path="settings/i18n" element={<RequirePermission requireTenantAdmin><I18nSettingsPage /></RequirePermission>} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="trust" element={<RequirePermission permission={Permissions.VIEW_BILLING}><TrustAccountingPage /></RequirePermission>} />
          <Route path="efiling" element={<RequirePermission permission={Permissions.MANAGE_MATTERS}><EFilingPage /></RequirePermission>} />
          <Route path="docgen" element={<RequirePermission permission={Permissions.MANAGE_MATTERS}><DocGenPage /></RequirePermission>} />
          <Route path="intake" element={<RequirePermission permission={Permissions.MANAGE_MATTERS}><IntakeFormsPage /></RequirePermission>} />
          <Route path="reports" element={<RequirePermission permission={Permissions.VIEW_BILLING}><ReportsPage /></RequirePermission>} />
          <Route path="audit" element={<RequirePermission permission={Permissions.MANAGE_USERS}><AuditPage /></RequirePermission>} />
          <Route path="tasks" element={<RequirePermission permission={Permissions.VIEW_MATTERS}><TasksPage /></RequirePermission>} />
          <Route path="conflicts" element={<RequirePermission permission={Permissions.VIEW_CLIENTS}><ConflictSearchPage /></RequirePermission>} />
          <Route path="bulk" element={<RequirePermission permission={Permissions.MANAGE_CLIENTS}><BulkOperationsPage /></RequirePermission>} />
          <Route path="social" element={<RequirePermission permission={Permissions.MANAGE_BOT}><SocialConnectionsPage /></RequirePermission>} />
          <Route path="social-posts" element={<RequirePermission permission={Permissions.MANAGE_BOT}><SocialPostsPage /></RequirePermission>} />
          <Route path="content-studio" element={<RequirePermission permission={Permissions.MANAGE_BOT}><ContentStudioPage /></RequirePermission>} />
          <Route path="marketing-analytics" element={<RequirePermission permission={Permissions.MANAGE_BOT}><MarketingAnalyticsPage /></RequirePermission>} />
          <Route path="lead-scoring" element={<RequirePermission permission={Permissions.MANAGE_BOT}><LeadScoringPage /></RequirePermission>} />
          <Route path="dashboard-widgets" element={<DashboardWidgetsPage />} />
          <Route path="expenses" element={<RequirePermission permission={Permissions.VIEW_BILLING}><ExpensesPage /></RequirePermission>} />
          <Route path="knowledge-base" element={<RequirePermission permission={Permissions.VIEW_MATTERS}><KnowledgeBasePage /></RequirePermission>} />
          <Route path="court-deadlines" element={<RequirePermission permission={Permissions.MANAGE_MATTERS}><CourtDeadlineCalculatorPage /></RequirePermission>} />
          <Route path="signatures" element={<RequirePermission permission={Permissions.MANAGE_MATTERS}><SignaturesPage /></RequirePermission>} />
          <Route path="communications" element={<RequirePermission permission={Permissions.VIEW_CLIENTS}><CommunicationLogPage /></RequirePermission>} />
          <Route path="campaign-social" element={<RequirePermission permission={Permissions.MANAGE_BOT}><CampaignSocialPage /></RequirePermission>} />
          <Route path="growth" element={<GrowthIndexPage />} />
          <Route path="growth/playbooks" element={<PlaybooksIndexPage />} />
          <Route path="growth/playbooks/:playbookId" element={<PlaybookDetailPage />} />
          <Route path="users" element={<RequirePermission permission={Permissions.MANAGE_USERS}><UsersPage /></RequirePermission>} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<RequirePermission requireTenantAdmin><SettingsPage /></RequirePermission>} />
          <Route path="settings/integrations" element={<IntegrationsSettingsPage />} />
          <Route path="import" element={<RequirePermission permission={Permissions.MANAGE_CLIENTS}><ImportWizardPage /></RequirePermission>} />
          <Route path="settings/custom-fields" element={<RequirePermission requireTenantAdmin><CustomFieldsSettings /></RequirePermission>} />
          <Route path="settings/quickbooks" element={<RequirePermission requireTenantAdmin><QuickBooksSettingsPage /></RequirePermission>} />
          <Route path="settings/twilio" element={<RequirePermission requireTenantAdmin><TwilioSettingsPage /></RequirePermission>} />
          <Route path="settings/api-keys" element={<RequirePermission requireTenantAdmin><ApiKeysPage /></RequirePermission>} />
          <Route path="settings/webhooks" element={<RequirePermission requireTenantAdmin><WebhooksPage /></RequirePermission>} />
          <Route path="settings/vendors" element={<RequirePermission requireTenantAdmin><VendorsPage /></RequirePermission>} />
          <Route path="settings/compliance-calendar" element={<RequirePermission requireTenantAdmin><ComplianceCalendarPage /></RequirePermission>} />
          <Route path="settings/mfa" element={<MfaSettingsPage />} />
          <Route path="sms" element={<RequirePermission permission={Permissions.VIEW_CLIENTS}><SmsPage /></RequirePermission>} />
          <Route path="contract-review" element={<RequirePermission permission={Permissions.MANAGE_MATTERS}><ContractReviewList /></RequirePermission>} />
          <Route path="contract-review/:reviewId" element={<RequirePermission permission={Permissions.MANAGE_MATTERS}><ContractReviewDetail /></RequirePermission>} />
          <Route path="kanban" element={<RequirePermission permission={Permissions.VIEW_MATTERS}><KanbanPage /></RequirePermission>} />
          <Route path="billing/payment-plans" element={<RequirePermission permission={Permissions.VIEW_BILLING}><PaymentPlansPage /></RequirePermission>} />
          <Route path="approvals" element={<RequirePermission permission={Permissions.MANAGE_MATTERS}><ApprovalsPage /></RequirePermission>} />
          <Route path="legal-research" element={<RequirePermission permission={Permissions.MANAGE_MATTERS}><LegalResearchPage /></RequirePermission>} />
          <Route path="legal-research/:researchId" element={<RequirePermission permission={Permissions.VIEW_MATTERS}><LegalResearchDetailPage /></RequirePermission>} />
          <Route path="analytics/revenue" element={<RequirePermission permission={Permissions.VIEW_BILLING}><RevenueAnalyticsPage /></RequirePermission>} />
          <Route path="activity" element={<RequirePermission permission={Permissions.VIEW_MATTERS}><ActivityFeedPage /></RequirePermission>} />
          <Route path="billing/rate-card" element={<RequirePermission permission={Permissions.VIEW_BILLING}><RateCardPage /></RequirePermission>} />
        </Route>

        {/* Admin routes */}
        <Route
          path="admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="tenants" element={<AdminTenantsPage />} />
          <Route path="tenants/:tenantId" element={<AdminTenantDetailPage />} />
        </Route>

        {/* Public storefront */}
        <Route path="storefront/:accountId" element={<StorefrontPage />} />
        <Route path="storefront/:accountId/success" element={<StorefrontPage />} />

        {/* Public intake form */}
        <Route path="intake/:slug" element={<PublicIntakeForm />} />

        {/* 404 Catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      </StealthGate>
    </BrowserRouter>
    </ConfirmProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}
